/**
 * Upload Ephemeral File Component
 *
 * A compact file upload control that uploads files to the ephemeral storage API.
 * Returns both the file hash and original filename for use in downstream workflows.
 *
 * Features:
 * - Click or drag-and-drop file selection using BeatUI's FileInput
 * - Compact UI when not actively uploading (looks like a dropdown/select)
 * - Progress tracking during upload
 * - Error handling with retry capability
 * - Exposes uploaded file hash and filename via signals
 *
 * @example
 * ```ts
 * const uploader = UploadEphemeralFile({
 *   accept: 'image/*',
 *   maxFileSize: 10 * 1024 * 1024, // 10MB
 * })
 *
 * // Access uploaded file data
 * const hash = uploader.fileHash.get()
 * const filename = uploader.filename.get()
 * const isUploading = uploader.isUploading.get()
 * ```
 */

import { html, attr, prop, Ensure, on, When } from '@tempots/dom'
import { FileInput, Icon } from '@tempots/beatui'
import { files } from '@/services/gateway'
import type { EphemeralFileResponse } from '@/services/files.service'
import { AsyncResult } from '@tempots/std'
import { AsyncResultView } from '@tempots/ui'

// ============================================================================
// Types
// ============================================================================

export interface UploadEphemeralFileOptions {
  /** File type restrictions (e.g., 'image/*', '.pdf', 'image/png,image/jpeg') */
  accept?: string
  /** Maximum file size in bytes */
  maxFileSize?: number
  /** Placeholder text when no file is selected */
  placeholder?: string
  /** Disable the control */
  disabled?: boolean
  /** Called when upload completes successfully */
  onUploadComplete?: (result: EphemeralFileResponse) => void
  /** Called when upload fails */
  onUploadError?: (error: Error) => void
  /** Called when file is cleared */
  onClear?: () => void
}

// ============================================================================
// Component
// ============================================================================

export function UploadEphemeralFile(options: UploadEphemeralFileOptions = {}) {
  const {
    accept,
    maxFileSize,
    placeholder = 'Select a file to upload...',
    disabled = false,
    onUploadComplete,
    onUploadError,
    onClear,
  } = options

  // State
  const selectedFile = prop<File | undefined>(undefined)
  const filename = prop<string | null>(null)
  const fileRef = prop<string | null>(null)
  const uploadState = prop<AsyncResult<EphemeralFileResponse, Error>>(AsyncResult.notAsked)
  const uploadProgress = prop(0)
  const error = prop<string | null>(null)
  const uploadResult = prop<EphemeralFileResponse | null>(null)

  fileRef.on((ref) => {
    console.log('FileRef changed', ref)
  })
  selectedFile.on((file) => {
    console.log('Selected file changed', file)
  })

  // Derived state (disabled for now - re-enable when implementing full upload flow)
  // const isUploading = uploadState.map((s) => s === 'uploading')
  const isDisabled = prop(disabled)

  // Upload handler
  async function handleFileSelect(file: File | undefined) {
    if (!file) return

    // Validate file size if specified
    if (maxFileSize && file.size > maxFileSize) {
      const sizeMB = (maxFileSize / (1024 * 1024)).toFixed(1)
      error.set(`File size exceeds maximum of ${sizeMB}MB`)
      uploadState.set(AsyncResult.notAsked)
      selectedFile.set(undefined)
      return
    }

    selectedFile.set(file)
    filename.set(file.name)
    error.set(null)
    uploadState.set(AsyncResult.loading())
    uploadProgress.set(0)

    try {
      // Upload to ephemeral storage
      const result = await files.upload(file, {
        onProgress: (loaded, total) => {
          console.log(loaded, total)
          const percent = Math.round((loaded / total) * 100)
          uploadProgress.set(percent)
        },
      })

      // Store result - new API returns hash directly
      uploadResult.set(result)
      console.log(result)
      // For ephemeral files, fileRef is "e{hash}"
      fileRef.set(result.fileRef)

      // uploadProgress.set(100)
      uploadState.set(AsyncResult.success(result))

      // Notify success
      if (onUploadComplete) {
        onUploadComplete(result)
      }
    } catch (error) {
      uploadState.set(AsyncResult.failure(error as Error))
      // error.set('Failed to upload file. Please try again.')

      if (onUploadError) {
        onUploadError(new Error('Failed to upload file'))
      }
    }
  }

  uploadState.on((state) => {
    console.log('Upload state changed', state)
  })

  function handleClear() {
    console.log('Clear')
    selectedFile.set(undefined)
    // fileHash.set(null)
    filename.set(null)
    fileRef.set(null)
    uploadResult.set(null)
    uploadState.set(AsyncResult.notAsked)
    uploadProgress.set(0)
    error.set(null)
    if (onClear) {
      onClear()
    }
  }

  // Render component
  return html.div(
    attr.class('upload-ephemeral-file'),
    html.div(
      When(uploadState.map(v => !AsyncResult.isNotAsked(v)), () => attr.class('hidden')),
      // File input with compact mode
      FileInput({
        value: selectedFile,
        onChange: handleFileSelect,
        accept,
        maxFileSize,
        placeholder,
        disabled: isDisabled,
        mode: 'compact',
      }),
    ),

    // When idle or error: show file input
    AsyncResultView(uploadState, {
      notAsked: () =>
        html.div(
          attr.class('space-y-2'),

        ),
      failure: () =>
        html.div(
          attr.class('space-y-2'),

          // File input with compact mode
          FileInput({
            value: selectedFile,
            onChange: handleFileSelect,
            accept,
            maxFileSize,
            placeholder,
            disabled: isDisabled,
            mode: 'compact',
          }),

          // Error message
          Ensure(error, (err) =>
            html.div(
              attr.class('flex items-center gap-2 text-sm text-red-600'),
              Icon({ icon: 'mdi:alert-circle' }, attr.class('w-4 h-4')),
              html.span(err),
              html.button(
                attr.class(
                  'px-2 py-1 text-sm text-blue-600 hover:text-blue-800 transition-colors'
                ),
                on.click(handleClear),
                'Clear'
              )
            )
          )
        ),
      loading: () =>
        html.div(
          attr.class('space-y-2'),

          // File info
          html.div(
            attr.class(
              'flex items-center gap-2 p-3 border rounded-md bg-gray-50'
            ),
            Icon({ icon: 'mdi:file' }, attr.class('w-5 h-5 text-gray-600')),
            html.div(
              attr.class('flex-1 min-w-0'),
              html.div(
                attr.class('text-sm font-medium truncate'),
                filename.map((f) => f || '')
              ),
              html.div(
                attr.class('text-xs text-gray-500'),
                uploadProgress.map((p) => `Uploading... ${p}%`)
              )
            ),
            Icon(
              { icon: 'mdi:loading' },
              attr.class('w-5 h-5 text-blue-600 animate-spin')
            )
          ),

          // Progress bar
          html.div(
            attr.class('w-full h-2 bg-gray-200 rounded-full overflow-hidden'),
            html.div(
              attr.class('h-full bg-blue-600 transition-all duration-300'),
              attr.style(
                uploadProgress.map((p) => ({
                  width: `${p}%`,
                }))
              )
            )
          )
        ),
      success: () =>
        html.div(
          attr.class(
            'flex items-center gap-2 p-3 border border-green-200 rounded-md bg-green-50'
          ),
          Icon(
            { icon: 'mdi:check-circle' },
            attr.class('w-5 h-5 text-green-600')
          ),
          html.div(
            attr.class('flex-1 min-w-0'),
            html.div(
              attr.class('text-sm font-medium truncate text-green-900'),
              filename.map((f) => f || '')
            ),
            html.div(attr.class('text-xs text-green-700'), 'Upload complete')
          ),
          html.button(
            attr.class(
              'p-1 text-green-600 hover:text-green-800 transition-colors'
            ),
            attr.title('Remove file'),
            on.click(handleClear),
            Icon({ icon: 'mdi:close' }, attr.class('w-5 h-5'))
          )
        ),
    })
  )
}
